/**
 * Recurring PayPal Smart Buttons.
 *
 * Wraps the `vault=true&intent=subscription` SDK to render a "Subscribe"
 * button bound to one of our live Billing Plans. The createSubscription
 * callback POSTs to /api/subscriptions/paypal-create which:
 *   1. Talks to PayPal's REST API to mint a subscription resource.
 *   2. Inserts a local 'pending' row tied to the paypal_subscription_id.
 *   3. Returns the paypal_subscription_id for us to hand back to PayPal.
 *
 * After the user approves in the popup, PayPal fires
 * BILLING.SUBSCRIPTION.ACTIVATED to our webhook, which flips the local
 * row to active and sets users.is_subscribed=1. onApproved fires
 * client-side for UI feedback only — never depend on it for state.
 */

import React, { useEffect, useRef, useState } from 'react';
import './SubscribeButtons.css';
import { subscriptionsAPI } from '../../services/api';

declare global {
  interface Window {
    paypal_sub_sdk?: any;
  }
}

export interface SubscribeButtonsProps {
  planType: 'monthly' | 'quarterly' | 'yearly';
  /** Optional — pass when the caller is signed in. Otherwise the
   *  backend falls back to the bearer-token session lookup. */
  userId?: string;
  /** Required for the fallback path (no bearer token). */
  email?: string;
  displayName?: string;
  onApproved?: (paypalSubscriptionId: string) => void;
  onError?: (err: unknown) => void;
}

const SubscribeButtons: React.FC<SubscribeButtonsProps> = ({
  planType,
  userId,
  email,
  displayName,
  onApproved,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renderedRef = useRef(false);
  const [ready, setReady] = useState(typeof window !== 'undefined' && !!window.paypal_sub_sdk);
  const [err, setErr] = useState<string | null>(null);

  // Wait for the deferred SDK script to finish loading.
  useEffect(() => {
    if (ready) return;
    if (typeof window === 'undefined') return;
    const t = window.setInterval(() => {
      if (window.paypal_sub_sdk) {
        setReady(true);
        window.clearInterval(t);
      }
    }, 200);
    return () => window.clearInterval(t);
  }, [ready]);

  // Render the buttons once the SDK is up and we have a mount point.
  useEffect(() => {
    if (!ready || !containerRef.current || renderedRef.current) return;
    const paypal = window.paypal_sub_sdk;
    if (!paypal?.Buttons) return;

    renderedRef.current = true;
    containerRef.current.innerHTML = '';

    paypal
      .Buttons({
        style: { shape: 'rect', color: 'gold', layout: 'vertical', label: 'subscribe' },
        createSubscription: async () => {
          try {
            const res = await subscriptionsAPI.paypalCreate({
              planType,
              userId,
              email,
              displayName,
            });
            if (!res?.paypalSubscriptionId) {
              throw new Error('No paypalSubscriptionId returned');
            }
            return res.paypalSubscriptionId;
          } catch (e: any) {
            console.error('[SubscribeButtons] paypalCreate failed', e);
            setErr(e?.message || 'Could not start subscription.');
            throw e;
          }
        },
        onApprove: (data: any) => {
          onApproved?.(data?.subscriptionID);
        },
        onError: (e: any) => {
          console.error('[SubscribeButtons] PayPal error', e);
          setErr('PayPal error — please try again.');
          renderedRef.current = false; // allow re-render on next mount
          onError?.(e);
        },
        onCancel: () => {
          // User closed the popup without approving — silent.
        },
      })
      .render(containerRef.current)
      .catch((e: any) => {
        console.error('[SubscribeButtons] render failed', e);
        setErr('Could not load PayPal buttons.');
        renderedRef.current = false;
      });
  }, [ready, planType, userId, email, displayName, onApproved, onError]);

  return (
    <div className="subscribe-buttons">
      {!ready && (
        <div className="subscribe-buttons-loading">
          <span className="spinner" /> Loading PayPal…
        </div>
      )}
      <div ref={containerRef} aria-label={`Subscribe to ${planType} plan`} />
      {err && <div className="subscribe-buttons-error">{err}</div>}
    </div>
  );
};

export default SubscribeButtons;
